import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  XMarkIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { useCreateTemplate, useUpdateTemplate } from '../../hooks/useMessageTemplates';
import type { TemplateCreatorModalProps, TemplateFormData } from './TemplateCreatorModal.types';
import { TEMPLATE_VARIABLES, VARIABLE_CATEGORIES, ROLE_OPTIONS } from './TemplateCreatorModal.types';
import type { TemplateType } from '../../types/tracker.types';
import './TemplateCreatorModal.styles.css';

const DEFAULT_FORM_DATA: TemplateFormData = {
  name: '',
  type: 'sms',
  content: '',
  subject: '',
  preselect_role: 'demandeur',
  fallback_greeting: 'Madame, Monsieur',
};

export const TemplateCreatorModal: React.FC<TemplateCreatorModalProps> = ({
  isOpen,
  onClose,
  onSave,
  editTemplate,
}) => {
  const [formData, setFormData] = useState<TemplateFormData>(DEFAULT_FORM_DATA);
  const [error, setError] = useState<string | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { createTemplate, isCreating } = useCreateTemplate();
  const { updateTemplate, isUpdating } = useUpdateTemplate();

  const isEditing = !!editTemplate;
  const isSaving = isCreating || isUpdating;

  // Initialize form when editing
  useEffect(() => {
    if (editTemplate) {
      setFormData({
        name: editTemplate.name,
        type: editTemplate.type,
        content: editTemplate.content,
        subject: editTemplate.subject || '',
        preselect_role: editTemplate.preselect_role,
        fallback_greeting: editTemplate.fallback_greeting,
      });
    } else {
      setFormData(DEFAULT_FORM_DATA);
    }
    setError(null);
  }, [editTemplate, isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSaving) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isSaving, onClose]);

  // Insert variable at cursor position
  const insertVariable = useCallback((placeholder: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.content;
    const newText = text.substring(0, start) + placeholder + text.substring(end);

    setFormData(prev => ({ ...prev, content: newText }));

    // Restore focus and move cursor after inserted text
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + placeholder.length, start + placeholder.length);
    }, 0);
  }, [formData.content]);

  // Handle form submission
  const handleSubmit = async () => {
    // Validation
    if (!formData.name.trim()) {
      setError('Le nom du modèle est requis');
      return;
    }
    if (!formData.content.trim()) {
      setError('Le contenu du message est requis');
      return;
    }

    setError(null);

    try {
      let savedTemplate;
      if (isEditing && editTemplate) {
        savedTemplate = await updateTemplate(editTemplate.id, {
          name: formData.name.trim(),
          type: formData.type,
          content: formData.content.trim(),
          subject: formData.type === 'email' ? formData.subject.trim() || undefined : undefined,
          preselect_role: formData.preselect_role,
          fallback_greeting: formData.fallback_greeting.trim() || 'Madame, Monsieur',
        });
      } else {
        savedTemplate = await createTemplate({
          name: formData.name.trim(),
          type: formData.type,
          content: formData.content.trim(),
          subject: formData.type === 'email' ? formData.subject.trim() || undefined : undefined,
          preselect_role: formData.preselect_role,
          fallback_greeting: formData.fallback_greeting.trim() || 'Madame, Monsieur',
        });
      }

      if (savedTemplate) {
        onSave(savedTemplate);
        onClose();
      }
    } catch (err) {
      setError('Erreur lors de la sauvegarde du modèle');
    }
  };

  // Calculate SMS length
  const smsLength = formData.content.length;
  const smsCount = smsLength <= 160 ? 1 : Math.ceil(smsLength / 153);

  if (!isOpen) return null;

  return createPortal(
    <div className="template-modal-overlay" onClick={isSaving ? undefined : onClose}>
      <div
        className="template-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="template-modal-title"
      >
        {/* Header */}
        <div className="template-modal__header">
          <h2 id="template-modal-title" className="template-modal__title">
            {isEditing ? 'Modifier le modèle' : 'Créer un modèle'}
          </h2>
          <button
            type="button"
            className="template-modal__close"
            onClick={onClose}
            disabled={isSaving}
            aria-label="Fermer"
          >
            <XMarkIcon />
          </button>
        </div>

        {/* Content */}
        <div className="template-modal__content">
          {/* Name input */}
          <div className="template-modal__field">
            <label htmlFor="template-name" className="template-modal__label">
              Nom du modèle
            </label>
            <input
              id="template-name"
              type="text"
              className="template-modal__input"
              placeholder="ex: Rappel RDV"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              disabled={isSaving}
              autoFocus
            />
          </div>

          {/* Type selector */}
          <div className="template-modal__field">
            <label className="template-modal__label">Type</label>
            <div className="template-modal__type-toggle">
              <button
                type="button"
                className={`template-modal__type-btn ${formData.type === 'sms' ? 'active' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, type: 'sms' as TemplateType }))}
                disabled={isSaving}
              >
                <DevicePhoneMobileIcon />
                <span>SMS</span>
              </button>
              <button
                type="button"
                className={`template-modal__type-btn ${formData.type === 'email' ? 'active' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, type: 'email' as TemplateType }))}
                disabled={isSaving}
              >
                <EnvelopeIcon />
                <span>Email</span>
              </button>
            </div>
          </div>

          {/* Subject input (email templates only) */}
          {formData.type === 'email' && (
            <div className="template-modal__field">
              <label htmlFor="template-subject" className="template-modal__label">
                Objet de l'email
              </label>
              <input
                id="template-subject"
                type="text"
                className="template-modal__input"
                placeholder="ex: Expertise {reference} — {adresse}"
                value={formData.subject}
                onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                disabled={isSaving}
              />
            </div>
          )}

          {/* Variables by category */}
          <div className="template-modal__field">
            <label className="template-modal__label">Variables disponibles</label>
            <div className="template-modal__variables-categories">
              {VARIABLE_CATEGORIES.map((category) => {
                const categoryVariables = TEMPLATE_VARIABLES.filter(v => v.category === category.id);
                return (
                  <div key={category.id} className="template-modal__category">
                    <span className="template-modal__category-label">{category.label}</span>
                    <div className="template-modal__variables">
                      {categoryVariables.map((variable) => (
                        <button
                          key={variable.id}
                          type="button"
                          className="template-modal__variable-btn"
                          onClick={() => insertVariable(variable.placeholder)}
                          disabled={isSaving}
                          title={variable.description}
                        >
                          + {variable.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Content textarea */}
          <div className="template-modal__field">
            <label htmlFor="template-content" className="template-modal__label">
              Message
            </label>
            <textarea
              id="template-content"
              ref={textareaRef}
              className="template-modal__textarea"
              placeholder="Bonjour {destinataire}, ..."
              value={formData.content}
              onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
              disabled={isSaving}
              rows={5}
            />
            {formData.type === 'sms' && (
              <div className="template-modal__char-count">
                <span className={smsLength > 160 ? 'warning' : ''}>
                  {smsLength}/160
                </span>
                {smsCount > 1 && (
                  <span className="template-modal__sms-count">
                    ({smsCount} SMS)
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Preselect role */}
          <div className="template-modal__field">
            <label htmlFor="template-role" className="template-modal__label">
              Présélectionner le destinataire
            </label>
            <select
              id="template-role"
              className="template-modal__select"
              value={formData.preselect_role || ''}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                preselect_role: (e.target.value || null) as typeof formData.preselect_role
              }))}
              disabled={isSaving}
            >
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value || 'none'} value={option.value || ''}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Fallback greeting */}
          {formData.preselect_role && (
            <div className="template-modal__field">
              <label htmlFor="template-fallback" className="template-modal__label">
                Si non trouvé, utiliser
              </label>
              <input
                id="template-fallback"
                type="text"
                className="template-modal__input"
                placeholder="Madame, Monsieur"
                value={formData.fallback_greeting}
                onChange={(e) => setFormData(prev => ({ ...prev, fallback_greeting: e.target.value }))}
                disabled={isSaving}
              />
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="template-modal__error">
              <ExclamationCircleIcon />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="template-modal__footer">
          <button
            type="button"
            className="template-modal__btn template-modal__btn--secondary"
            onClick={onClose}
            disabled={isSaving}
          >
            Annuler
          </button>
          <button
            type="button"
            className="template-modal__btn template-modal__btn--primary"
            onClick={handleSubmit}
            disabled={isSaving}
          >
            {isSaving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TemplateCreatorModal;
